import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ShieldCheck,
  Leaf,
  Heart,
  Users,
  Sparkles,
  Star,
  Smile,
  Apple,
  Lightbulb,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useState, type FormEvent } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useT } from "@/lib/i18n/provider";
import truweKidsLogo from "@/assets/truwe-kids-logo.webp";
import truweKidsKoala from "@/assets/truwe-kids-koala.webp";

export const Route = createFileRoute("/kids")({
  component: KidsPage,
  head: () => ({
    meta: [
      { title: "TruWe Kids. Happy Kids. Healthy Future." },
      {
        name: "description",
        content:
          "TruWe Kids: tasty, no-added-sugar gummies with research-backed ingredients. Brain+ and Eye+ for growing bodies and minds.",
      },
      { property: "og:title", content: "TruWe Kids. Happy Kids. Healthy Future." },
      {
        property: "og:description",
        content: "Clean, science-backed gummies for kids. Safe, tasty, parent-trusted.",
      },
      { property: "og:url", content: "https://truwe.in/kids" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [
      { rel: "canonical", href: "https://truwe.in/kids" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "TruWe Kids. Happy Kids. Healthy Future.",
          url: "https://truwe.in/kids",
          description: "Clean, science-backed gummies for kids, no added sugar, research-backed ingredients.",
          isPartOf: { "@id": "https://truwe.in/#website" },
          about: { "@id": "https://truwe.in/#organization" },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://truwe.in/" },
            { "@type": "ListItem", position: 2, name: "Kids", item: "https://truwe.in/kids" },
          ],
        }),
      },
    ],
  }),
});



// Brand palette — TruWe Kids brand guidelines (locked)
// Primary: #2B8673 green · #FF6B68 coral · #FFC83D yellow · #7B61D1 purple · #0F1D3D navy
const KIDS = {
  blue: "#2B8673", // primary green
  pink: "#FF6B68", // coral
  orange: "#FFC83D", // yellow
  teal: "#2B8673", // green (badge accent)
  purple: "#7B61D1",
  navy: "#0F1D3D",
  cream: "#F2F7FF", // secondary cream-white (#F2F7FF)
};

const VALUES = [
  { icon: ShieldCheck, titleKey: "kids.value.safe.title", descKey: "kids.value.safe.desc", color: KIDS.pink, bg: "oklch(0.97 0.04 15)" },
  { icon: Leaf, titleKey: "kids.value.clean.title", descKey: "kids.value.clean.desc", color: KIDS.teal, bg: "oklch(0.97 0.04 180)" },
  { icon: Heart, titleKey: "kids.value.made.title", descKey: "kids.value.made.desc", color: KIDS.orange, bg: "oklch(0.98 0.05 75)" },
  { icon: Users, titleKey: "kids.value.every.title", descKey: "kids.value.every.desc", color: KIDS.purple, bg: "oklch(0.97 0.04 295)" },
] as const;

function KidsLogo({ className = "" }: { className?: string }) {
  return (
    <img
      src={truweKidsLogo}
      alt="TruWe Kids"
      loading="lazy"
      decoding="async"
      className={`block w-auto select-none ${className}`}
      draggable={false}
    />
  );
}

function KidsPage() {
  const t = useT();

  return (
    <div style={{ background: KIDS.cream, fontFamily: "Poppins, sans-serif" }}>
      {/* Top brand bar */}
      <div
        className="flex items-center justify-center gap-3 px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-white"
        style={{ background: KIDS.pink }}
      >
        <span>{t("kids.topbar.happy")}</span>
        <span className="opacity-60">·</span>
        <span>{t("kids.topbar.healthy")}</span>
      </div>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 -z-0"
          style={{
            background:
              "radial-gradient(45% 45% at 12% 18%, oklch(0.96 0.06 15) 0%, transparent 60%), radial-gradient(40% 40% at 88% 80%, oklch(0.96 0.06 180) 0%, transparent 60%), radial-gradient(40% 40% at 80% 15%, oklch(0.97 0.06 75) 0%, transparent 60%)",
          }}
        />
        {/* Floating shapes, desktop only to avoid collisions on mobile */}
        <Sparkles className="absolute left-[6%] top-[18%] hidden h-6 w-6 md:block" style={{ color: KIDS.orange }} />
        <Star className="absolute left-[10%] top-[60%] hidden h-5 w-5 md:block" style={{ color: KIDS.pink }} />
        <Apple className="absolute right-[8%] top-[12%] hidden h-7 w-7 md:block" style={{ color: KIDS.teal }} />
        <Smile className="absolute right-[12%] bottom-[18%] hidden h-6 w-6 md:block" style={{ color: KIDS.purple }} />

        <div className="relative mx-auto grid max-w-7xl gap-10 px-4 py-16 md:grid-cols-[1fr_1.1fr] md:items-center md:gap-12 md:px-8 md:py-24">
          <div className="md:order-2">
            <span
              className="inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1 text-[11px] font-extrabold uppercase tracking-[0.18em] shadow-sm"
              style={{ color: KIDS.pink }}
            >
              <Sparkles className="h-3.5 w-3.5" /> {t("kids.hero.badge")}
            </span>
            <KidsLogo className="mt-5 h-40 md:h-56" />
            <h1 className="mt-4 font-display text-3xl font-bold leading-tight md:text-5xl" style={{ color: KIDS.navy }}>
              <span style={{ color: KIDS.pink }}>{t("kids.hero.heading.happy")}</span>
              <br />
              <span style={{ color: KIDS.navy }}>{t("kids.hero.heading.healthy")}</span>
              <span style={{ color: KIDS.orange }}>{t("kids.hero.heading.future")}</span>
            </h1>
            <p className="mt-5 max-w-lg text-base md:text-lg" style={{ color: "oklch(0.4 0.04 265)" }}>
              {t("kids.hero.desc")}
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <span
                className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-extrabold uppercase tracking-[0.18em] text-white shadow-[0_10px_30px_-10px_oklch(0.7_0.18_15/0.6)]"
                style={{ background: KIDS.pink }}
              >
                <Sparkles className="h-4 w-4" /> {t("kids.hero.launchingSoon")}
              </span>
              <a href="#values">
                <Button
                  size="lg"
                  variant="outline"
                  className="rounded-full border-2 px-7 text-base font-extrabold"
                  style={{ borderColor: KIDS.navy, color: KIDS.navy }}
                >
                  {t("kids.hero.whyBtn")}
                </Button>
              </a>
            </div>
          </div>

          {/* Hero "Launching soon" badge */}
          <div className="relative flex items-center justify-center md:order-1">
            <div
              className="relative aspect-square w-full max-w-md overflow-hidden rounded-[2.5rem] ring-4 ring-white shadow-[0_30px_60px_-25px_rgba(15,42,90,0.45)]"
              style={{
                WebkitMaskImage:
                  "radial-gradient(130% 100% at 50% 45%, #000 58%, rgba(0,0,0,0.85) 72%, rgba(0,0,0,0.35) 88%, transparent 100%)",
                maskImage:
                  "radial-gradient(130% 100% at 50% 45%, #000 58%, rgba(0,0,0,0.85) 72%, rgba(0,0,0,0.35) 88%, transparent 100%)",
              }}
            >
              <img
                src={truweKidsKoala}
                alt={t("kids.hero.imgAlt")}
                className="absolute inset-0 h-full w-full object-cover"
                draggable={false}
              />
              {/* feathered vignette to blend edges into card */}
              <div
                className="pointer-events-none absolute inset-0"
                style={{
                  background:
                    "radial-gradient(110% 75% at 50% 45%, transparent 50%, oklch(0.98 0.01 80 / 0.35) 80%, oklch(0.98 0.01 80 / 0.85) 100%)",
                }}
              />
              <Sparkles className="absolute right-[12%] top-[14%] h-9 w-9 text-white/85 drop-shadow" />
              <Star className="absolute left-[10%] top-[10%] h-8 w-8 text-white/80 drop-shadow" />
              <div className="absolute inset-x-0 top-5 flex justify-center">
                <p className="rounded-full bg-white/90 px-3 py-1 text-[10px] font-extrabold uppercase tracking-[0.28em]" style={{ color: KIDS.navy }}>
                  {t("kids.hero.comingYear")}
                </p>
              </div>
            </div>
            {/* Floating badge */}
            <div
              className="absolute -bottom-4 left-1/2 -translate-x-1/2 rounded-full px-5 py-2 text-xs font-extrabold uppercase tracking-wider text-white shadow-lg"
              style={{ background: KIDS.teal }}
            >
              {t("kids.hero.noAddedSugar")}
            </div>
          </div>
        </div>
      </section>

      {/* Brand values */}
      <section id="values" className="mx-auto max-w-7xl px-4 py-16 md:px-8 md:py-20">
        <div className="mx-auto max-w-2xl text-center">
          <p className="text-[11px] font-extrabold uppercase tracking-[0.22em]" style={{ color: KIDS.pink }}>
            {t("kids.values.eyebrow")}
          </p>
          <h2 className="mt-2 font-display text-3xl font-bold md:text-5xl" style={{ color: KIDS.navy }}>
            {t("kids.values.heading.pre")}<span style={{ color: KIDS.orange }}>{t("kids.values.heading.safe")}</span>{t("kids.values.heading.mid1")}
            <span style={{ color: KIDS.teal }}>{t("kids.values.heading.tasty")}</span>{t("kids.values.heading.mid2")}
            <span style={{ color: KIDS.purple }}>{t("kids.values.heading.effective")}</span>{t("kids.values.heading.end")}
          </h2>
        </div>
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {VALUES.map((v) => (
            <div
              key={v.titleKey}
              className="rounded-[1.75rem] border-2 border-white bg-white p-6 text-center shadow-[0_10px_30px_-15px_rgba(15,42,90,0.2)] transition hover:-translate-y-1"
            >
              <div
                className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl"
                style={{ background: v.bg, color: v.color }}
              >
                <v.icon className="h-8 w-8" strokeWidth={1.75} />
              </div>
              <h3 className="mt-4 font-display text-lg font-bold" style={{ color: v.color }}>
                {t(v.titleKey)}
              </h3>
              <p className="mt-1.5 text-sm" style={{ color: "oklch(0.45 0.03 265)" }}>
                {t(v.descKey)}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Launching soon */}
      <section id="products" className="relative">
        <div
          className="absolute inset-0 -z-0"
          style={{
            background:
              "linear-gradient(180deg, transparent 0%, oklch(0.97 0.03 350) 50%, transparent 100%)",
          }}
        />
        <div className="relative mx-auto max-w-5xl px-4 py-16 md:px-8 md:py-24">
          <div className="overflow-hidden rounded-[2.5rem] border-2 border-white bg-white px-6 py-14 text-center shadow-[0_20px_50px_-25px_rgba(15,42,90,0.25)] md:px-12 md:py-20">
            <span
              className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-[11px] font-extrabold uppercase tracking-[0.22em] text-white"
              style={{ background: KIDS.pink }}
            >
              <Sparkles className="h-3.5 w-3.5" /> {t("kids.launch.badge")}
            </span>
            <h2 className="mt-6 font-display text-4xl font-bold md:text-6xl" style={{ color: KIDS.navy }}>
              {t("kids.launch.heading.pre")}<span style={{ color: KIDS.orange }}>{t("kids.launch.heading.tasty")}</span>{t("kids.launch.heading.mid")}
              <span style={{ color: KIDS.teal }}>{t("kids.launch.heading.coming")}</span>{t("kids.launch.heading.end")}
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-base md:text-lg" style={{ color: "oklch(0.45 0.03 265)" }}>
              {t("kids.launch.desc")}
            </p>
            <KidsNotifyForm />
            <p className="mt-4 text-xs uppercase tracking-[0.2em] text-muted-foreground">
              {t("kids.launch.noSpam")}
            </p>
          </div>
        </div>
      </section>

      {/* Promise band */}
      <section className="mx-auto max-w-7xl px-4 pb-20 md:px-8">
        <div
          className="overflow-hidden rounded-[2.5rem] px-6 py-10 text-white md:px-12 md:py-14"
          style={{
            background: `linear-gradient(135deg, ${KIDS.pink} 0%, ${KIDS.orange} 100%)`,
          }}
        >
          <div className="flex flex-col items-start gap-6 md:flex-row md:items-center md:justify-between">
            <div>
              <p className="text-[11px] font-extrabold uppercase tracking-[0.22em] text-white/85">
                {t("kids.promise.eyebrow")}
              </p>
              <div className="mt-3 inline-flex items-center justify-center rounded-2xl bg-white/95 px-4 py-2 shadow-sm">
                <KidsLogo className="h-14 md:h-16" />
              </div>
              <h2 className="mt-3 font-display text-3xl font-bold md:text-4xl">
                {t("kids.promise.heading")}
              </h2>
              <p className="mt-2 max-w-xl text-white/90">
                {t("kids.promise.line")}
              </p>
            </div>
            <a href="#products">
              <Button
                size="lg"
                className="rounded-full bg-white px-7 text-base font-extrabold hover:bg-white/90"
                style={{ color: KIDS.pink }}
              >
                {t("kids.promise.shopBtn")}
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Parent suggestion box */}
      <section className="mx-auto max-w-7xl px-4 pb-24 md:px-8" id="suggest">
        <div
          className="overflow-hidden rounded-[2.5rem] border-2 bg-white p-6 md:p-12"
          style={{ borderColor: "oklch(0.92 0.02 265)" }}
        >
          <div className="grid gap-10 md:grid-cols-[1fr,1.2fr] md:items-center">
            <div>
              <span
                className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-[11px] font-extrabold uppercase tracking-[0.2em] text-white"
                style={{ background: KIDS.purple }}
              >
                <Lightbulb className="h-3.5 w-3.5" /> {t("kids.suggest.badge")}
              </span>
              <h2
                className="mt-4 font-display text-3xl font-bold md:text-4xl"
                style={{ color: KIDS.navy }}
              >
                {t("kids.suggest.heading")}
              </h2>
              <p className="mt-3 max-w-md text-base" style={{ color: "oklch(0.45 0.03 265)" }}>
                {t("kids.suggest.desc")}
              </p>
              <ul className="mt-6 space-y-2 text-sm" style={{ color: KIDS.navy }}>
                <li className="flex items-start gap-2">
                  <Sparkles className="mt-0.5 h-4 w-4" style={{ color: KIDS.teal }} />
                  {t("kids.suggest.point1")}
                </li>
                <li className="flex items-start gap-2">
                  <Heart className="mt-0.5 h-4 w-4" style={{ color: KIDS.pink }} />
                  {t("kids.suggest.point2")}
                </li>
              </ul>
            </div>
            <KidsSuggestForm />
          </div>
        </div>
      </section>

      {/* Related collections, internal linking */}
      <section aria-labelledby="kids-related" className="mx-auto max-w-7xl px-4 pb-24 md:px-8">
        <h2 id="kids-related" className="font-display text-2xl font-bold md:text-3xl" style={{ color: KIDS.navy }}>
          {t("kids.related.heading")}
        </h2>
        <ul className="mt-6 grid gap-3 sm:grid-cols-2 md:grid-cols-3">
          {[
            { slug: "kids-nutrition", labelKey: "kids.related.kidsNutrition" },
            { slug: "magnesium-supplements", labelKey: "kids.related.magnesium" },
            { slug: "sleep-recovery", labelKey: "kids.related.sleep" },
            { slug: "inositol-supplements", labelKey: "kids.related.inositol" },
            { slug: "pcos-nutrition", labelKey: "kids.related.pcos" },
            { slug: "liver-gut-support", labelKey: "kids.related.liverGut" },
          ].map((c) => (
            <li key={c.slug}>
              <Link
                to="/collections/$slug"
                params={{ slug: c.slug as "kids-nutrition" }}
                className="block rounded-2xl border-2 bg-white px-5 py-4 text-sm font-semibold transition hover:shadow-sm"
                style={{ borderColor: "oklch(0.92 0.02 265)", color: KIDS.navy }}
              >
                {t(c.labelKey as Parameters<typeof t>[0])} <span style={{ color: KIDS.pink }}>→</span>
              </Link>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

function KidsSuggestForm() {
  const t = useT();
  const [productIdea, setProductIdea] = useState("");
  const [reason, setReason] = useState("");
  const [ageRange, setAgeRange] = useState("");
  const [parentName, setParentName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const AGE_OPTIONS = ["2-4 yrs", "5-7 yrs", "8-10 yrs", "11-14 yrs", "15+ yrs"];

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const idea = productIdea.trim();
    const trimmedEmail = email.trim().toLowerCase();
    if (idea.length < 2) {
      toast.error(t("kids.form.suggest.errIdea"));
      return;
    }
    if (trimmedEmail && !EMAIL_RE.test(trimmedEmail)) {
      toast.error(t("kids.form.suggest.errEmail"));
      return;
    }
    setLoading(true);
    const { error } = await supabase.from("kids_product_suggestions").insert({
      product_idea: idea,
      reason: reason.trim() || null,
      child_age_range: ageRange || null,
      parent_name: parentName.trim() || null,
      email: trimmedEmail || null,
    });
    setLoading(false);
    if (error) {
      toast.error(t("kids.form.suggest.errGeneric"));
      return;
    }
    setDone(true);
    toast.success(t("kids.form.suggest.toastSuccess"));
  };

  if (done) {
    return (
      <div
        className="rounded-2xl border-2 bg-[oklch(0.98_0.02_155)] p-8 text-center"
        style={{ borderColor: KIDS.teal }}
      >
        <div
          className="mx-auto flex h-14 w-14 items-center justify-center rounded-full text-white"
          style={{ background: KIDS.teal }}
        >
          <Sparkles className="h-6 w-6" />
        </div>
        <p className="mt-4 font-display text-xl font-bold" style={{ color: KIDS.navy }}>
          {t("kids.form.suggest.doneHeading")}
        </p>
        <p className="mt-2 text-sm" style={{ color: "oklch(0.45 0.03 265)" }}>
          {t("kids.form.suggest.doneDesc")}
        </p>
        <button
          type="button"
          onClick={() => {
            setDone(false);
            setProductIdea("");
            setReason("");
            setAgeRange("");
            setParentName("");
            setEmail("");
          }}
          className="mt-5 text-sm font-bold underline"
          style={{ color: KIDS.purple }}
        >
          {t("kids.form.suggest.suggestAnother")}
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="space-y-3">
      <div>
        <label className="text-xs font-bold uppercase tracking-wider" style={{ color: KIDS.navy }}>
          {t("kids.form.suggest.ideaLabel")}
        </label>
        <input
          required
          value={productIdea}
          onChange={(e) => setProductIdea(e.target.value)}
          maxLength={200}
          placeholder={t("kids.form.suggest.ideaPlaceholder")}
          className="mt-1.5 h-12 w-full rounded-2xl border-2 border-[oklch(0.92_0.02_265)] bg-white px-4 text-sm font-semibold outline-none focus:border-[color:var(--kids-purple,#7B61D1)]"
          style={{ color: KIDS.navy }}
        />
      </div>

      <div>
        <label className="text-xs font-bold uppercase tracking-wider" style={{ color: KIDS.navy }}>
          {t("kids.form.suggest.reasonLabel")}
        </label>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          maxLength={600}
          rows={3}
          placeholder={t("kids.form.suggest.reasonPlaceholder")}
          className="mt-1.5 w-full rounded-2xl border-2 border-[oklch(0.92_0.02_265)] bg-white px-4 py-3 text-sm font-medium outline-none focus:border-[color:var(--kids-purple,#7B61D1)]"
          style={{ color: KIDS.navy }}
        />
      </div>

      <div>
        <label className="text-xs font-bold uppercase tracking-wider" style={{ color: KIDS.navy }}>
          {t("kids.form.suggest.ageLabel")}
        </label>
        <div className="mt-2 flex flex-wrap gap-2">
          {AGE_OPTIONS.map((opt) => {
            const active = ageRange === opt;
            return (
              <button
                type="button"
                key={opt}
                onClick={() => setAgeRange(active ? "" : opt)}
                className="rounded-full border-2 px-3.5 py-1.5 text-xs font-bold transition"
                style={{
                  borderColor: active ? KIDS.purple : "oklch(0.92 0.02 265)",
                  background: active ? KIDS.purple : "white",
                  color: active ? "white" : KIDS.navy,
                }}
              >
                {opt}
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <input
          value={parentName}
          onChange={(e) => setParentName(e.target.value)}
          maxLength={80}
          placeholder={t("kids.form.suggest.namePlaceholder")}
          autoComplete="name"
          className="h-12 rounded-2xl border-2 border-[oklch(0.92_0.02_265)] bg-white px-4 text-sm font-semibold outline-none focus:border-[color:var(--kids-purple,#7B61D1)]"
          style={{ color: KIDS.navy }}
        />
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          maxLength={320}
          placeholder={t("kids.form.suggest.emailPlaceholder")}
          autoComplete="email"
          className="h-12 rounded-2xl border-2 border-[oklch(0.92_0.02_265)] bg-white px-4 text-sm font-semibold outline-none focus:border-[color:var(--kids-purple,#7B61D1)]"
          style={{ color: KIDS.navy }}
        />
      </div>

      <Button
        type="submit"
        size="lg"
        disabled={loading}
        className="h-12 w-full rounded-full px-7 text-sm font-extrabold text-white hover:opacity-90 disabled:opacity-60"
        style={{ background: KIDS.purple }}
      >
        {loading ? t("kids.form.suggest.submitting") : t("kids.form.suggest.submitBtn")}
      </Button>
      <p className="text-center text-[11px] text-muted-foreground">
        {t("kids.form.suggest.disclaimer")}
      </p>
    </form>
  );
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^(?:\+?91[\-\s]?|0)?[6-9]\d{9}$/;

function KidsNotifyForm() {
  const t = useT();
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const trimmedEmail = email.trim().toLowerCase();
    const trimmedPhone = phone.trim().replace(/\s|-/g, "");
    if (!EMAIL_RE.test(trimmedEmail)) {
      toast.error(t("kids.form.notify.errEmail"));
      return;
    }
    if (trimmedPhone && !PHONE_RE.test(trimmedPhone)) {
      toast.error(t("kids.form.notify.errPhone"));
      return;
    }
    setLoading(true);
    const { error } = await supabase.from("reservations").insert({
      email: trimmedEmail,
      phone: trimmedPhone || null,
      source: "kids-launching-soon",
    });
    setLoading(false);
    if (error) {
      if (error.code === "23505") {
        setDone(true);
        toast.success(t("kids.form.notify.toastAlready"));
        return;
      }
      toast.error(t("kids.form.notify.errGeneric"));
      return;
    }
    setDone(true);
    toast.success(t("kids.form.notify.toastSuccess"));
  };

  if (done) {
    return (
      <div
        className="mx-auto mt-8 max-w-md rounded-2xl border-2 px-6 py-5 text-center"
        style={{ borderColor: KIDS.pink, background: "white" }}
      >
        <p className="font-display text-lg font-bold" style={{ color: KIDS.navy }}>
          {t("kids.form.notify.doneHeading")}
        </p>
        <p className="mt-1 text-sm" style={{ color: "oklch(0.45 0.03 265)" }}>
          {t("kids.form.notify.doneDesc")}
        </p>
      </div>
    );
  }

  return (
    <form
      className="mx-auto mt-8 flex max-w-md flex-col gap-3"
      onSubmit={onSubmit}
    >
      <input
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        aria-label={t("kids.form.notify.emailLabel")}
        placeholder={t("kids.form.notify.emailPlaceholder")}
        maxLength={320}
        autoComplete="email"
        className="h-12 rounded-full border-2 border-[oklch(0.92_0.02_265)] bg-white px-5 text-sm font-semibold outline-none focus:border-[var(--kids-pink)]"
        style={{ color: KIDS.navy }}
      />
      <input
        type="tel"
        value={phone}
        onChange={(e) => setPhone(e.target.value)}
        aria-label={t("kids.form.notify.phoneLabel")}
        placeholder={t("kids.form.notify.phonePlaceholder")}
        maxLength={20}
        inputMode="tel"
        autoComplete="tel"
        className="h-12 rounded-full border-2 border-[oklch(0.92_0.02_265)] bg-white px-5 text-sm font-semibold outline-none focus:border-[var(--kids-pink)]"
        style={{ color: KIDS.navy }}
      />
      <Button
        type="submit"
        size="lg"
        disabled={loading}
        className="h-12 rounded-full px-7 text-sm font-extrabold text-white hover:opacity-90 disabled:opacity-60"
        style={{ background: KIDS.pink }}
      >
        {loading ? t("kids.form.notify.submitting") : t("kids.form.notify.submitBtn")}
      </Button>
    </form>
  );
}
